import { useMemo, useState } from "react"
import type { ApolloError } from "@apollo/client"
import { formatDateString } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import Separator from "@/v2-ui/separator"
import { DotText } from "@/v2-backoffice-client/components/dot"
import RelativeDate from "@/v2-backoffice-client/components/RelativeDate"
import Table, {
  createColumns,
  GROUPING_RANGE_DATES,
  type TableProps,
} from "@/v2-backoffice-client/components/table"
import { formatUserName } from "@/v2-backoffice-client/user/user.utils"
import type { UserTenantFragment } from "@/v2-backoffice-client/user/tenant/__types__/user.tenant.fragment"
import TenantUserManageButton from "@/v2-backoffice-client/tenant/user/manage/TenantUserManageButton"
import UserCreateManyButton from "@/v2-backoffice-client/user/create/many/UserCreateManyButton"
import TenantUserTableSelectionToolbar from "@/v2-backoffice-client/tenant/user/TenantUserTableSelectionToolbar"
import TenantUserTableRowToolbar from "@/v2-backoffice-client/tenant/user/TenantUserTableRowToolbar"
import UserActivityBadge from "@/v2-backoffice-client/user/activity/UserActivityBadge"
import UserStatusBadge from "@/v2-backoffice-client/user/status/UserStatusBadge"
import UserContactSyncCell from "@/v2-backoffice-client/user/contact/UserContactSyncCell"
import {
  CONTACT_SYNC_STATUS_CLIENT_FILTER_OPTIONS,
  getContactSyncStatusLabel,
} from "@/v2-backoffice-client/user/contact/userContact.constants"
import UserMigrationCell from "@/v2-backoffice-client/user/migration/UserMigrationCell"
import {
  ACTIVATION_STATUS_CLIENT_FILTER_OPTIONS,
  getActivationStatusLabel,
} from "@/v2-backoffice-client/tenant/migration/tenantMigration.constants"
import TruncatedList from "@/v2-backoffice-client/components/truncatedList"
import CustomerOwnershipBadge from "@/v2-backoffice-client/customer/ownership/CustomerOwnershipBadge"
import { getCrossCustomerMembership } from "@/v2-backoffice-client/customer/ownership/customer.ownership.utils"
import { buildUserUsageColumns } from "@/v2-backoffice-client/usage/usage.columns"
import {
  buildUserLicenseColumns,
  buildUserSeatsColumn,
} from "@/v2-backoffice-client/user/user.columns"
import {
  useLicenseGoverningStatuses,
  type LicenseStatusFilterOption,
} from "@/v2-backoffice-client/license/license.statuses.hooks"
import {
  groupSeatProductsByUser,
  type LicenseSeatHolder,
} from "@/v2-backoffice-client/license/seats/license.seats.utils"

const ACTIVE_STATUS_CODE = 0
const DELETED_STATUS_CODE = 1

const COLUMN_SIZING = {
  "User.Name": 175,
  "UserTenantType.Name": 80,
  UserStatusCode: 90,
  UserActivityLevelCode: 80,
  ContactSyncStatus: 120,
  MigrationStatus: 130,
  IdentityCreatedAt: 130,
  LastSignInAt: 130,
  CustomerNames: 150,
}

type CreateTenantUserColumnsInput = {
  tenantId?: number
  /** Null when no member holds a seat: the column is left out. */
  seatProductsByUserId: Map<number, string[]> | null
  licenseStatusNamesByCode: Map<number, string>
  /** As labels: this table filters client-side against the label. */
  licenseStatusOptions: LicenseStatusFilterOption[]
}

function createTenantUserColumns(input: CreateTenantUserColumnsInput) {
  const {
    tenantId,
    seatProductsByUserId,
    licenseStatusNamesByCode,
    licenseStatusOptions,
  } = input
  return createColumns<UserTenantFragment>({
    enableSelection: true,
    enableSorting: true,
    enableHiding: true,
    columns: {
      "User.Name": {
        id: "User.Name",
        label: "Name",
        getValue: (row) =>
          formatUserName(row.User?.FirstName, row.User?.LastName),
        to: ({ row }) => `>>?v=user:${row.original.UserId}`,
      },
      UserStatusCode: {
        id: "UserStatusCode",
        label: "Status",
        getValue: (row) => row.User?.UserStatusCode,
        enableGrouping: true,
        renderCell({ row }) {
          const user = row.original.User
          if (!user) return null
          return <UserStatusBadge status={user.UserStatus} />
        },
        exportValue({ row }) {
          return row.original.User?.UserStatus.Name ?? null
        },
      },
      UserActivityLevelCode: {
        id: "UserActivityLevelCode",
        label: "Activity",
        getValue: (row) => row.User?.UserActivityLevelCode,
        enableGrouping: true,
        filter: {
          type: "select",
          options: [
            { label: "Daily", value: "1" },
            { label: "Weekly", value: "2" },
            { label: "Monthly", value: "3" },
            { label: "Inactive", value: "4" },
            { label: "Unknown", value: "5" },
          ],
        },
        renderCell({ row }) {
          const user = row.original.User
          if (!user) return null
          return <UserActivityBadge level={user.UserActivityLevel} />
        },
        exportValue({ row }) {
          return row.original.User?.UserActivityLevel.Name ?? null
        },
      },
      "User.Email": {
        id: "User.Email",
        label: "Email",
        defaultHidden: true,
        renderCell({ row }) {
          return row.original.User?.Email ?? null
        },
      },
      // HubSpot link state for each member (badge tooltip carries the
      // "Sync to HubSpot" repair action).
      ContactSyncStatus: {
        id: "ContactSyncStatus",
        label: "HubSpot",
        enableGrouping: true,
        // Client-side filtering compares against getValue — the label.
        filter: {
          type: "multi-select",
          placeholder: "Any HubSpot status",
          options: CONTACT_SYNC_STATUS_CLIENT_FILTER_OPTIONS,
        },
        getValue: (row) =>
          getContactSyncStatusLabel(row.User?.ContactSyncStatus),
        renderCell({ row }) {
          const user = row.original.User
          if (!user) return null
          return <UserContactSyncCell user={user} showHealthy />
        },
        exportValue({ row }) {
          return getContactSyncStatusLabel(row.original.User?.ContactSyncStatus)
        },
      },
      // The FUS-927 migration target group: who of this team's members has
      // actually used v2. Client-filtered against the label like the other
      // columns of this table.
      UsesV2: {
        id: "UsesV2",
        label: "V2 User",
        enableGrouping: true,
        filter: {
          type: "select",
          placeholder: "V2 usage",
          options: [
            { label: "V2 user", value: "Yes" },
            { label: "Never used v2", value: "No" },
          ],
        },
        getValue: (row) => (row.User?.UsesV2 ? "Yes" : "No"),
        renderCell({ row }) {
          const rowUser = row.original.User
          if (!rowUser) return null
          return (
            <DotText variant={rowUser.UsesV2 ? "success-1" : "muted"}>
              {rowUser.UsesV2 ? "Yes" : "No"}
            </DotText>
          )
        },
        exportValue({ row }) {
          return row.original.User?.UsesV2 ? "Yes" : "No"
        },
      },
      // Fusion-login migration state (FUS-927 CS-managed waves), read off
      // the nested User fragment like every other user-shaped table.
      MigrationStatus: {
        id: "MigrationStatus",
        label: "Fusion Login",
        enableGrouping: true,
        filter: {
          type: "multi-select",
          placeholder: "Any migration status",
          options: ACTIVATION_STATUS_CLIENT_FILTER_OPTIONS,
        },
        getValue: (row) => getActivationStatusLabel(row.User?.MigrationStatus),
        renderCell({ row }) {
          const rowUser = row.original.User
          if (!rowUser?.MigrationStatus) return null
          return <UserMigrationCell user={rowUser} tenantId={tenantId} />
        },
        exportValue({ row }) {
          return getActivationStatusLabel(row.original.User?.MigrationStatus)
        },
      },
      "UserTenantType.Name": {
        id: "UserTenantType.Name",
        label: "Role",
        enableGrouping: true,
        filter: {
          type: "select",
          placeholder: "Filter by role...",
          options: [
            { label: "Admin", value: "1" },
            { label: "User", value: "2" },
            { label: "Viewer", value: "3" },
          ],
        },
      },
      // Same usage signals as the top-level Users board; the deep link
      // stacks the user panel open on its Usage tab.
      ...buildUserUsageColumns<UserTenantFragment>({
        getUsage: (row) => row.User,
        buildTo: (row) => `>>?v=user:${row.UserId}&user-view-tabs-tab=usage`,
      }),
      IdentityCreatedAt: {
        id: "IdentityCreatedAt",
        label: "Sign-up",
        defaultHidden: true,
        getValue: (row) => row.User?.IdentityCreatedAt,
        renderCell({ row }) {
          return <RelativeDate date={row.original.User?.IdentityCreatedAt} />
        },
        exportValue({ row }) {
          return formatDateString(row.original.User?.IdentityCreatedAt)
        },
      },
      LastSignInAt: {
        id: "LastSignInAt",
        label: "Last Sign-in",
        getValue: (row) => row.User?.LastSignInAt,
        groupingRanges: GROUPING_RANGE_DATES,
        renderCell({ row }) {
          return <RelativeDate date={row.original.User?.LastSignInAt} />
        },
        exportValue({ row }) {
          return formatDateString(row.original.User?.LastSignInAt)
        },
      },
      CustomerNames: {
        id: "CustomerNames",
        label: "Customer",
        description:
          "The customer that owns the member (whose licenses and seats apply to them). Normally the team's own customer; a member owned by another customer is flagged.",
        enableGrouping: true,
        getValue: (row) => row.User?.CustomerNames?.[0] ?? "",
        renderCell({ row }) {
          const crossing = getCrossCustomerMembership(
            row.original.User,
            row.original.Tenant,
          )
          return (
            <span className="inline-flex items-center gap-2">
              <TruncatedList
                items={(row.original.User?.Customers ?? []).map((customer) => ({
                  label: customer.Name,
                  to: `>>?v=customer:${customer.ExternalCustomerId}`,
                }))}
              />
              {crossing && (
                <CustomerOwnershipBadge
                  crossing={crossing}
                  perspective="team"
                />
              )}
            </span>
          )
        },
        exportValue({ row }) {
          return row.original.User?.CustomerNames?.join(", ") ?? ""
        },
      },
      // The license posture of each member's owning customer, the same
      // three columns as the Users board. A team's members are nearly
      // always one customer's; where one is another's, this is where it
      // shows.
      ...buildUserLicenseColumns<UserTenantFragment>({
        getUser: (row) => row.User,
        statusNamesByCode: licenseStatusNamesByCode,
        statusOptions: licenseStatusOptions,
      }),
      // The seats each member holds on their customer's seated licenses.
      // Present only while some member holds one.
      ...(seatProductsByUserId &&
        buildUserSeatsColumn<UserTenantFragment>({
          getUserId: (row) => row.UserId,
          seatProductsByUserId,
        })),
      Id: {
        id: "Id",
        label: "User Team Id",
        defaultSize: 110,
        defaultHidden: true,
      },
    },
  })
}

export type TenantUserTableProps = Omit<
  TableProps<UserTenantFragment>,
  "data" | "columns"
> & {
  tenantId?: number
  tenantName?: string
  isLoading?: boolean
  error?: ApolloError | Error
  userTenants: UserTenantFragment[]
  /** Seats held across the members' customers' seated licenses, for the Seats column. */
  seatHolders?: LicenseSeatHolder[]
  activeCount?: number
  removedCount?: number
  onDeletedFilterChange?(onlyDeleted: boolean | undefined): void
  onRefresh?(): void
}

function TenantUserTable(props: TenantUserTableProps) {
  const {
    className,
    tenantId,
    tenantName,
    userTenants,
    seatHolders,
    activeCount,
    removedCount,
    onDeletedFilterChange,
    onRefresh,
    isLoading,
    error,
    ...tableProps
  } = props

  const [onlyDeleted, setOnlyDeleted] = useState(false)
  const { namesByCode: licenseStatusNamesByCode, labelOptions } =
    useLicenseGoverningStatuses()
  const columns = useMemo(
    () =>
      createTenantUserColumns({
        tenantId,
        seatProductsByUserId:
          seatHolders && seatHolders.length > 0
            ? groupSeatProductsByUser(seatHolders)
            : null,
        licenseStatusNamesByCode,
        licenseStatusOptions: labelOptions,
      }),
    [tenantId, seatHolders, licenseStatusNamesByCode, labelOptions],
  )

  return (
    <Table
      className={cn("tenant-user-table", className)}
      tableId="tenant-user-table"
      data={userTenants}
      columns={columns}
      initialColumnSizing={COLUMN_SIZING}
      enableGrouping
      enableRowSelection
      {...tableProps}
    >
      <Table.Toolbar>
        <Table.StatusFilter
          defaultValue={ACTIVE_STATUS_CODE}
          onChange={(value) => {
            const deleted = value === DELETED_STATUS_CODE
            setOnlyDeleted(deleted)
            onDeletedFilterChange?.(value === null ? undefined : deleted)
          }}
        >
          <Table.StatusFilter.Option
            value={null}
            count={
              activeCount !== undefined && removedCount !== undefined
                ? activeCount + removedCount
                : undefined
            }
          >
            All
          </Table.StatusFilter.Option>

          <Table.StatusFilter.Option
            variant="success"
            value={ACTIVE_STATUS_CODE}
            count={activeCount}
          >
            Active
          </Table.StatusFilter.Option>

          <Table.StatusFilter.Option
            variant="destructive"
            value={DELETED_STATUS_CODE}
            count={removedCount}
          >
            Removed
          </Table.StatusFilter.Option>
        </Table.StatusFilter>

        <div className="flex-1" />

        <Table.ToolbarFilters />
        <Separator orientation="vertical" />

        {/* Global search: name, email — standardized across every
            user-shaped table. */}
        <Table.Search placeholder="Search by name or email…" />
        <Separator orientation="vertical" />

        <Table.ToolbarMenu onRefresh={onRefresh} exportFileName="tenant-users">
          {tenantId && !onlyDeleted && (
            <TenantUserManageButton
              tenantId={tenantId}
              tenantName={tenantName}
              existingUserTenants={userTenants}
            />
          )}
          {tenantId && !onlyDeleted && (
            <UserCreateManyButton
              defaultTenantId={tenantId}
              defaultTenantName={tenantName}
            />
          )}
        </Table.ToolbarMenu>
      </Table.Toolbar>

      <Table.FilterList />
      <Table.GroupList />

      <Table.RowToolbar<UserTenantFragment> column="User.Name">
        {(ut) => (
          <TenantUserTableRowToolbar
            userTenant={ut}
            tenantId={tenantId ?? 0}
            onlyDeleted={onlyDeleted}
            onMembershipChanged={onRefresh}
          />
        )}
      </Table.RowToolbar>

      <Table.Content countLabel="members" isLoading={isLoading} error={error} />

      <Table.SelectionToolbar<UserTenantFragment>>
        {(uts, reset) => (
          <TenantUserTableSelectionToolbar
            uts={uts}
            tenant={
              tenantId
                ? { Id: tenantId, Name: tenantName ?? `Team ${tenantId}` }
                : undefined
            }
            reset={reset}
            onlyDeleted={onlyDeleted}
            onMembershipChanged={onRefresh}
          />
        )}
      </Table.SelectionToolbar>
    </Table>
  )
}

export default TenantUserTable
